import * as yup from 'yup';
import {
    alphaNumeric,
    numberRegex
} from '../../../utils/regex';
import dayjs from '../../../utils/dayjs';
import { MPOWER_ACCOUNT_TYPE_MASTER, PRICE_TYPE_DROPDOWN_LIST, UNLISTED_EQUITY_ORDER_DATE_MIN_RANGE } from '../../../utils/constant';
// NOTE: MPOWER_ACCOUNT_TYPE_MASTER is keyed by account type codes (e.g. "POA", "HLDAWY") and verified to exist in utils/constant.
// NOTE: UNLISTED_EQUITY_ORDER_DATE_MIN_RANGE is a numeric constant (days) verified in utils/constant.

// getCleanPriceLabel: actively used 4 times in Bonds baseValidations (lines ~882, 892, 902, 912 in this file)
// to build dynamic error messages like "Clean Price is required" / "Dirty Price should be > 0" based on priceType.
// Do NOT remove — it is not dead code despite being defined near the top of this file.
const getCleanPriceLabel = (priceType?: string) =>
    PRICE_TYPE_DROPDOWN_LIST.find(obj => obj.value === (priceType ?? ""))?.key ?? "Clean Price";

export const baseInitialValues = {
    "Unlisted Equity": {
        // Client Details
        clientName: null,
        account: "",
        accountType: "",
        modeOfHolding: "",
        investorBankAccount: null,
        bankSubAccountType: "",
        emailId: "",
        srm: "",
        availableBankBalance: "",
        utilizedBalance: "",
        netAvailableBalance: "",
        // Order Details
        // orderNumber: "",
        orderDate: dayjs().startOf('day'),
        security: null,
        issuer: "",
        counterParty: null,
        transactionType: "",
        quantity: "",
        rate: "",
        stampDuty: false,
        charges: "",
        amount: "",
        remarks: "",
        // tradeCalculationData: {},
        // Settlement Details
        settlementMethod: "",
        settlementDate: dayjs().startOf('day'),
        referenceNumber: "",
        settlementBankSource: "",
        settlementBank: "",
        settlementBankNumber: null,
        settlementBankIfsc: "",
        settlementDetailsDepositoryName: "",
        settlementDetailsDpNameList: [],
        settlementDetailsDpName: null,
        settlementDetailsDpId: "",
        settlementDetailsClientId: "",
        // Order Supporting Document
        documents: [{
            documentId: "",
            documentType: ""
        }]
    },
    AIF: {
        // Client Details
        clientName: null,
        account: "",
        accountType: "",
        modeOfHolding: "",
        investorBankAccount: null,
        bankSubAccountType: "",
        emailId: "",
        srm: "",
        availableBankBalance: "",
        utilizedBalance: "",
        netAvailableBalance: "",
        // Order Details
        // orderNumber: "",
        orderDate: dayjs().startOf('day'),
        security: null,
        selectedFolioData: null, // stores the full folio object selected from folioNoList
        folioNumber: "",
        issuer: "",
        transactionType: "",
        quantity: "",
        lastAvailableNav: "",
        currentMarketValue: "",
        commitmentAmount: "",
        amount: "",
        redeemType: "Quantity",
        redeemAll: false,
        deferralException: "No",
        remarks: "",
        // Settlement Details
        settlementMethod: "",
        settlementDate: dayjs().startOf('day'),
        referenceNumber: "",
        settlementBank: "",
        settlementBankNumber: null,
        settlementBankIfsc: "",
        settlementDetailsDepositoryName: "",
        settlementDetailsDpNameList: [],
        settlementDetailsDpName: null,
        settlementDetailsDpId: "",
        settlementDetailsClientId: "",
        // Order Supporting Document
        documents: [{
            documentId: "",
            documentType: ""
        }]
    },
    PMS: {
        // Client Details
        clientName: null,
        account: "",
        accountType: "",
        modeOfHolding: "",
        investorBankAccount: null,
        bankSubAccountType: "",
        emailId: "",
        srm: "",
        availableBankBalance: "",
        utilizedBalance: "",
        netAvailableBalance: "",
        // Order Details
        // orderNumber: "",
        orderDate: dayjs().startOf('day'),
        security: null,
        selectedFolioData: null, // stores the full folio object selected from folioNoList
        folioNumber: "",
        issuer: "",
        transactionType: "",
        amount: "",
        currentPortfolioValue: "",
        lastValuationDate: "",
        remarks: "",
        // Settlement Details
        settlementMethod: "",
        settlementDate: dayjs().startOf('day'),
        referenceNumber: "",
        settlementBank: "",
        settlementBankNumber: null,
        settlementBankIfsc: "",
        // Order Supporting Document
        documents: [{
            documentId: "",
            documentType: ""
        }]
    },
    Bonds: {
        // Client Details
        clientName: null,
        account: "",
        accountType: "",
        modeOfHolding: "",
        investorBankAccount: null,
        bankSubAccountType: "",
        emailId: "",
        srm: "",
        availableBankBalance: "",
        utilizedBalance: "",
        netAvailableBalance: "",
        uccCode: "",
        // dpId: "",
        // Order Details
        // orderNumber: "",
        orderDate: dayjs().startOf('day'),
        security: null,
        assetClass: "",
        counterParty: null,
        broker: null,
        issuer: "",
        transactionType: "",
        acquisitionCost: "",
        quantity: "",
        priceType: "dirtyPrice",
        cleanPrice: "",
        stampDuty: "",
        accruedInterest: "",
        settlementAmount: "",
        remarks: "",
        tradeCalculationData: {},
        // Settlement Details
        settlementMethod: "",
        settlementDate: dayjs().startOf('day'),
        referenceNumber: "",
        settlementBankSource: "",
        settlementBank: "",
        settlementBankNumber: null,
        settlementBankIfsc: "",
        settlementDetailsDepositoryName: "",
        settlementDetailsDpNameList: [],
        settlementDetailsDpName: null,
        settlementDetailsDpId: "",
        settlementDetailsClientId: "",
        // Order Supporting Document
        documents: [{
            documentId: "",
            documentType: ""
        }]
    },
    "Margin Money": {
        // Client Details
        clientName: null,
        account: "",
        accountType: "",
        modeOfHolding: "",
        investorBankAccount: null,
        bankSubAccountType: "",
        emailId: "",
        srm: "",
        availableBankBalance: "",
        utilizedBalance: "",
        netAvailableBalance: "",
        // Order Details
        // orderNumber: "",
        orderDate: dayjs().startOf('day'),
        transactionType: "",
        amount: "",
        broker: null,
        brokerBankDetails: null,
        remarks: "",
        // Settlement Details
        settlementMethod: "",
        settlementDate: dayjs().startOf('day'),
        referenceNumber: "",
        settlementBank: "",
        settlementBankNumber: null,
        settlementBankIfsc: "",
        // Order Supporting Document
        documents: [{
            documentId: "",
            documentType: ""
        }]
    },
    "Corpus In/Out": {
        // Client Details
        clientName: null,
        account: "",
        accountType: "",
        modeOfHolding: "",
        investorBankAccount: null,
        bankSubAccountType: "",
        emailId: "",
        srm: "",
        availableBankBalance: "",
        utilizedBalance: "",
        netAvailableBalance: "",
        // Order Details
        // orderNumber: "",
        orderDate: dayjs().startOf('day'),
        transactionType: "",
        amount: "",
        remarks: "",
        // Settlement Details
        settlementMethod: "",
        settlementDate: dayjs().startOf('day'),
        referenceNumber: "",
        settlementBank: "",
        settlementBankNumber: null,
        settlementBankIfsc: "",
        // Order Supporting Document
        documents: [{
            documentId: "",
            documentType: ""
        }]
    },
    "Fee Payment": {
        // Client Details
        clientName: null,
        account: "",
        accountType: "",
        modeOfHolding: "",
        investorBankAccount: null,
        bankSubAccountType: "",
        emailId: "",
        srm: "",
        availableBankBalance: "",
        utilizedBalance: "",
        netAvailableBalance: "",
        // Order Details
        // orderNumber: "",
        orderDate: dayjs().startOf('day'),
        transactionType: "",
        amount: "",
        remarks: "",
        // Settlement Details
        advisoryBankDetails: [],
        settlementMethod: "",
        settlementDate: dayjs().startOf('day'),
        referenceNumber: "",
        settlementBank: "",
        settlementBankNumber: null,
        settlementBankIfsc: "",
        // Order Supporting Document
        documents: [{
            documentId: "",
            documentType: ""
        }]
    }
}

export const baseValidations = {
    "Unlisted Equity": {
        // Client Details
        clientName: yup
            .object()
            .nullable()
            .test(
                "client-name-required",
                "Client Name is required",
                function (value) { return !!value }
            ),
        account: yup
            .string()
            .required("Account is required"),
        // accountType: yup.string().required("Account Type is required"), 
        investorBankAccount: yup
            .object()
            .nullable()
            .test(
                "investor-bank-account-required",
                "Investor Bank Account is required",
                function (value) { return !!value }
            ),
        // availableBankBalance: yup
        //     .number()
        //     .required('Available Bank Balance is required')
        //     .typeError("Available Bank Balance should be a valid number"),
        // emailId: yup
        //     .string()
        //     .required("Email ID is required")
        //     .matches(emailRegex, 'Invalid Email ID'), 
        // srm: yup
        //     .string()
        //     .required("RM is required"),
        // Order Details
        // orderNumber: yup.string().required("Order Number is required"),
        orderDate: yup
            .date()
            .required("Order Date is required")
            .test(
                "order-date-max",
                "Order Date cannot be greater than today's date",
                function (value) {
                    if (!value) return true

                    const today = dayjs().startOf('day')
                    const selectedDate = dayjs(value).startOf('day')

                    return selectedDate.isSameOrBefore(today)
                }
            )
            .test(
                "order-date-min-non-heldaway",
                `Order Date should be within the last ${UNLISTED_EQUITY_ORDER_DATE_MIN_RANGE} days from today's date`,
                function (value) {
                    const values = this.parent

                    const isHeldAwayAccountType = [MPOWER_ACCOUNT_TYPE_MASTER["HLDAWY"]].includes(MPOWER_ACCOUNT_TYPE_MASTER[`${values?.accountType ?? ""}`.toUpperCase()])

                    if (isHeldAwayAccountType) return true

                    if (!value) return true

                    const minDate = dayjs().startOf('day').subtract(UNLISTED_EQUITY_ORDER_DATE_MIN_RANGE, 'day')
                    const selectedDate = dayjs(value).startOf('day')

                    return selectedDate.isSameOrAfter(minDate)
                }
            ),
        security: yup
            .object()
            .nullable()
            .test(
                "security-required",
                "Security is required",
                function (value) { return !!value }
            ),
        // issuer: yup.string().required("Issuer is required"),
        counterParty: yup
            .object()
            .nullable()
            .test(
                "counter-party-required",
                "Counter Party is required",
                function (value) {
                    return !!value
                }),
        transactionType: yup
            .string()
            .required("Transaction Type is required"),
        quantity: yup
            .string()
            .required("Quantity is required")
            .test('is-number', 'Quantity should be a valid number', function (value) {
                return !Number.isNaN(Number(value))
            })
            .test('whole-number', 'Quantity should be a valid number', function (value) {
                return numberRegex.test(`${value ?? ""}`);
            }),
        rate: yup
            .string()
            .transform(function (value) {
                return `${value ?? ""}`?.replaceAll(',', '')
            })
            .required("Rate is required")
            .test('is-number', 'Rate should be a valid number', function (value) {
                return !Number.isNaN(Number(value))
            })
            .test('greater-than-zero', "Rate should be greater than 0", function (value) {
                return Number(value) > 0
            }),
        // .test('whole-number', 'Rate should be a valid number', function (value) {
        //     return numberRegex.test(`${value ?? ""}`);
        // })
        charges: yup
            .string()
            .transform(function (value) {
                return `${value ?? ""}`?.replaceAll(',', '')
            })
            .required("Charges is required")
            .test('is-number', 'Charges should be a valid number', function (value) {
                return !Number.isNaN(Number(value))
            }),
        // .test('greater-than-zero', "Charges should be greater than 0", function (value) {
        //     return Number(value) > 0
        // }),
        // .test('whole-number', 'Charges should be a valid number', function (value) {
        //     return numberRegex.test(`${value ?? ""}`);
        // }),
        amount: yup
            .string()
            .transform(function (value) {
                return `${value ?? ""}`?.replaceAll(',', '')
            })
            .required('Amount is required')
            .test('is-number', 'Amount should be a valid number', function (value) {
                return !Number.isNaN(Number(value))
            })
            .test('positive-number', 'Amount should be a positive number', function (value) {
                return Number(value ?? "") >= 0
            })
        ,
        // .test('greater-than-zero', "Amount should be greater than 0", function (value) {
        //     return Number(value) > 0
        // })
        // .test('whole-number', 'Amount should be a valid number', function (value) {
        //     return numberRegex.test(`${value ?? ""}`);
        // }),
        remarks: yup
            .string()
            .required("Remarks is required")
            .max(200, 'Max 200 characters allowed'),
        // Settlement Details
        settlementMethod: yup
            .string()
            .required("Settlement Method is required"),
        settlementDate: yup
            .date()
            .required("Settlement Date is required")
            .min(dayjs().startOf('day').toDate(), `Settlement Date cannot be less than today's date`),
        referenceNumber: yup
            .string()
            // .required("Reference Number is required")
            .matches(alphaNumeric, 'Invalid Reference Number'),
        // settlementBank: yup
        //     .string()
        //     .required("Settlement Bank is required"),
        settlementBankNumber: yup
            .object()
            .nullable()
            .test(
                "settlement-bank-number-required",
                "Settlement Bank Number is required",
                function (value) {
                    return !!value
                }),
        settlementDetailsDepositoryName: yup
            .string()
            .required("Depository Name is required"),
        settlementDetailsDpName: yup
            .object()
            .nullable()
            .test(
                "dp_name_required",
                "DP Name is required",
                function (value) {
                    return !!value
                }),
        settlementDetailsDpId: yup
            .string()
            .required("DP ID is required"),
        settlementDetailsClientId: yup
            .string()
            .required("Client ID is required"),
        // Order Supporting Document
        documents: yup
            .array()
            .of(
                yup.object().shape({
                    documentId: yup.string().required("Document Upload is required"),
                    documentType: yup.string().required("Document Type is required")
                })
            )
    },
    AIF: {
        // Client Details
        clientName: yup
            .object()
            .nullable()
            .test(
                "client-name-required",
                "Client Name is required",
                function (value) {
                    return !!value
                }),
        account: yup
            .string()
            .required("Account is required"),
        // accountType: yup.string().required("Account Type is required"), 
        investorBankAccount: yup
            .object()
            .nullable()
            .test(
                "investor-bank-account-required",
                "Investor Bank Account is required",
                function (value) { return !!value }
            ),
        // availableBankBalance: yup
        //     .number()
        //     .required('Available Bank Balance is required')
        //     .typeError("Available Bank Balance should be a valid number"),
        // emailId: yup
        //     .string()
        //     .required("Email ID is required")
        //     .matches(emailRegex, 'Invalid Email ID'), 
        // srm: yup
        //     .string()
        //     .required("RM is required"),
        // Order Details
        // orderNumber: yup.string().required("Order Number is required"),
        orderDate: yup
            .date()
            .required("Order Date is required")
            .max(dayjs().toDate(), `Order Date cannot be greater than today's date`),
        security: yup
            .object()
            .nullable()
            .test(
                "security-required",
                "Security is required",
                function (value) {
                    return !!value
                }
            ),
        folioNumber: yup
            .string()
            .when('transactionType', {
                is: 'Initial Contribution',
                then: (schema) => schema, // NOTE (CORR-35): no-op by design — Initial Contribution makes folioNumber optional (free-text, user may or may not enter it)
                otherwise: (schema) => schema
                    .required('Folio Number is required')
            }),
        transactionType: yup
            .string()
            .required("Transaction Type is required"),
        // quantity: yup
        //     .string()
        //     .required("Outstanding Units is required"),
        // lastAvailableNav: yup
        //     .string()
        //     .required("Last Available NAV is required"),
        // currentMarketValue: yup
        //     .number()
        //     .required('Current Market Value is required')
        //     .typeError("Current Market Value should be a valid number"),
        deferralException: yup
            .string()
            .required("Deferral Exception is required"),
        remarks: yup
            .string()
            .required("Remarks is required")
            .max(200, 'Max 200 characters allowed'),
        // Settlement Details
        settlementMethod: yup
            .string()
            .required("Settlement Method is required"),
        settlementDate: yup
            .date()
            .required("Settlement Date is required")
            .min(dayjs().startOf('day').toDate(), `Settlement Date cannot be less than today's date`),
        referenceNumber: yup
            .string()
            // .required("Reference Number is required")
            .matches(alphaNumeric, 'Invalid Reference Number'),
        // settlementBank: yup
        //     .string()
        //     .required("Settlement Bank is required"),
        settlementBankNumber: yup
            .object()
            .nullable()
            .test(
                "settlement-bank-number-required",
                "Settlement Bank Number is required",
                function (value) {
                    return !!value
                }),
        settlementDetailsDepositoryName: yup
            .string()
            .required("Depository Name is required"),
        settlementDetailsDpName: yup
            .object()
            .nullable()
            .test(
                "dp_name_required",
                "DP Name is required",
                function (value) {
                    return !!value
                }),
        settlementDetailsDpId: yup
            .string()
            .required("DP ID is required"),
        settlementDetailsClientId: yup
            .string()
            .required("Client ID is required"),
        // Order Supporting Document
        documents: yup
            .array()
            .of(
                yup.object().shape({
                    documentId: yup.string().required("Document Upload is required"),
                    documentType: yup.string().required("Document Type is required")
                })
            )
    },
    PMS: {
        // Client Details
        clientName: yup
            .object()
            .nullable()
            .test(
                "client-name-required",
                "Client Name is required",
                function (value) { return !!value }
            ),
        account: yup
            .string()
            .required("Account is required"),
        // accountType: yup.string().required("Account Type is required"),
        investorBankAccount: yup
            .object()
            .nullable()
            .test(
                "investor-bank-account-required",
                "Investor Bank Account is required",
                function (value) { return !!value }
            ),
        // availableBankBalance: yup
        //     .number()
        //     .required('Available Bank Balance is required')
        //     .typeError("Available Bank Balance should be a valid number"),
        // emailId: yup
        //     .string()
        //     .required("Email ID is required")
        //     .matches(emailRegex, 'Invalid Email ID'), 
        // srm: yup
        //     .string()
        //     .required("RM is required"),
        // Order Details
        // orderNumber: yup.string().required("Order Number is required"),
        orderDate: yup
            .date()
            .required("Order Date is required")
            .max(dayjs().toDate(), `Order Date cannot be greater than today's date`),
        security: yup
            .object()
            .nullable()
            .test(
                "security-required",
                "Security is required",
                function (value) { return !!value }
            ),
        folioNumber: yup
            .string()
            .when('transactionType', {
                is: 'Purchase',
                then: (schema) => schema,
                otherwise: (schema) => schema
                    .required('Folio Number is required')
            }),
        transactionType: yup
            .string()
            .required("Transaction Type is required"),
        amount: yup
            .string()
            .transform(function (value) {
                return `${value ?? ""}`?.replaceAll(',', '')
            })
            .required('Amount is required')
            .test('is-number', 'Amount should be a valid number', function (value) {
                return !Number.isNaN(Number(value))
            })
            .test('greater-than-zero', "Amount should be greater than 0", function (value) {
                return Number(value) > 0
            }),
        // .test('whole-number', 'Amount should be a valid number', function (value) {
        //     return numberRegex.test(`${value ?? ""}`);
        // }),
        // currentPortfolioValue: yup
        //     .number()
        //     .when('transactionType', {
        //         is: 'Withdrawal',
        //         then: yup
        //             .number()
        //             .required('Current Portfolio Value is required')
        //             .typeError("Current Portfolio Value should be a valid number"),
        //     }), 
        // lastValuationDate: yup
        //     .string()
        //     .when('transactionType', {
        //         is: 'Withdrawal',
        //         then: yup.string().required("Last Valuation Date is required"),
        //     }),
        remarks: yup
            .string()
            .required("Remarks is required")
            .max(200, 'Max 200 characters allowed'),
        // Settlement Details
        settlementMethod: yup
            .string()
            .required("Settlement Method is required"),
        settlementDate: yup
            .date()
            .required("Settlement Date is required")
            .min(dayjs().startOf('day').toDate(), `Settlement Date cannot be less than today's date`),
        referenceNumber: yup
            .string()
            // .required("Reference Number is required")
            .matches(alphaNumeric, 'Invalid Reference Number'),
        // settlementBank: yup
        //     .string()
        //     .required("Settlement Bank is required"),
        settlementBankNumber: yup
            .object()
            .nullable()
            .test(
                "settlement-bank-number-required",
                "Settlement Bank Number is required",
                function (value) {
                    return !!value
                }),
        // Order Supporting Document
        documents: yup.array().of(
            yup.object().shape({
                documentId: yup.string().required("Document Upload is required"),
                documentType: yup.string().required("Document Type is required")
            })
        )
    },
    Bonds: {
        // Client Details
        clientName: yup
            .object()
            .nullable()
            .test(
                "client-name-required",
                "Client Name is required",
                function (value) {
                    return !!value
                }),
        account: yup.string().required("Account is required"),
        // accountType: yup.string().required("Account Type is required"),
        investorBankAccount: yup
            .object()
            .nullable()
            .test(
                "investor-bank-account-required",
                "Investor Bank Account is required",
                function (value) { return !!value }
            ),
        // availableBankBalance: yup
        //     .number()
        //     .required('Available Bank Balance is required')
        //     .typeError("Available Bank Balance should be a valid number"),
        // emailId: yup
        //     .string()
        //     .required("Email ID is required")
        //     .matches(emailRegex, 'Invalid Email ID'), 
        // srm: yup.string().required("RM is required"),
        // uccCode: yup.string().required("UCC Code is required"),
        // dpId: yup.string().required("DP ID is required"),
        // Order Details
        // orderNumber: yup.string().required("Order Number is required"),
        orderDate: yup
            .date()
            .required("Order Date is required")
            .test(
                "order-date-max",
                "Order Date cannot be greater than today's date",
                function (value) {
                    if (!value) return true

                    const today = dayjs().startOf('day')
                    const selectedDate = dayjs(value).startOf('day')

                    return selectedDate.isSameOrBefore(today)
                }
            )
            .test(
                "order-date-min-non-heldaway",
                `Order Date should be within the last ${UNLISTED_EQUITY_ORDER_DATE_MIN_RANGE} days from today's date`,
                function (value) {
                    const values = this.parent

                    const isHeldAwayAccountType = [MPOWER_ACCOUNT_TYPE_MASTER["HLDAWY"]].includes(MPOWER_ACCOUNT_TYPE_MASTER[`${values?.accountType ?? ""}`.toUpperCase()])

                    if (isHeldAwayAccountType) return true

                    if (!value) return true

                    const minDate = dayjs().startOf('day').subtract(UNLISTED_EQUITY_ORDER_DATE_MIN_RANGE, 'day')
                    const selectedDate = dayjs(value).startOf('day')

                    return selectedDate.isSameOrAfter(minDate)
                }
            ),
        security: yup
            .object()
            .nullable()
            .test(
                "security-required",
                "Security is required",
                function (value) {
                    return !!value
                }),
        counterParty: yup
            .object()
            .nullable()
            .test(
                "counter-party-required",
                "Counter Party is required",
                function (value) {
                    return !!value
                }),
        // broker: yup
        //     .object()
        //     .nullable()
        //     .test(
        //         "broker-required",
        //         "Broker is required",
        //         function (value) { return !!value }
        //     ),
        // issuer: yup.string().required("Issuer is required"),
        transactionType: yup.string().required("Transaction Type is required"),
        // acquisitionCost: yup
        //     .string()
        //     .transform(function (value) {
        //         return `${value ?? ""}`?.replaceAll(',', '')
        //     })
        //     .required('Acquisition Cost is required')
        //     .test('is-number', 'Acquisition Cost should be a valid number', function (value) {
        //         return !Number.isNaN(Number(value))
        //     }),
        quantity: yup
            .string()
            .required("Quantity is required")
            .test('is-number', 'Quantity should be a valid number', function (value) {
                return !Number.isNaN(Number(value))
            })
            .test('whole-number', 'Quantity should be a valid number', function (value) {
                return numberRegex.test(`${value ?? ""}`);
            }),
        cleanPrice: yup
            .string()
            .transform(function (value) { return `${value ?? ""}`?.replaceAll(',', '') })
            .test('required', (value, context) => {
                const { createError, parent } = context;

                const currentLabel = getCleanPriceLabel(parent?.priceType ?? "");

                if (`${value ?? ""}`.length === 0) {
                    return createError({ message: `${currentLabel} is required` });
                }
                return true;
            })
            .test('is-number', (value, context) => {
                const { createError, parent } = context;

                const currentLabel = getCleanPriceLabel(parent?.priceType ?? "");

                if (Number.isNaN(Number(value))) {
                    return createError({ message: `${currentLabel} should be a valid number` });
                }
                return true;
            })
            .test('greater-than-zero', (value, context) => {
                const { createError, parent } = context;

                const currentLabel = getCleanPriceLabel(parent?.priceType ?? "");

                if (Number(value) <= 0) {
                    return createError({ message: `${currentLabel} should be greater than 0` });
                }
                return true;
            })
            .test('max-decimal-points', (value, context) => {
                const { createError, parent } = context;

                const currentLabel = getCleanPriceLabel(parent?.priceType ?? "");

                const stringValue = `${value ?? ""}`;

                // Skip when empty or not a valid number; those cases are handled by the 'required' and 'is-number' tests above, so we avoid duplicate errors.
                if (stringValue.length === 0 || Number.isNaN(Number(stringValue))) {
                    return true;
                }

                // Count the digits after the decimal point (if any) and fail when they exceed 10.
                const decimalPart = stringValue.split('.')[1] ?? "";
                if (decimalPart.length > 10) {
                    return createError({ message: `${currentLabel} can have a maximum of 10 decimal places` });
                }

                return true;
            }),
        // .test('whole-number', 'Clean Price should be a valid number', function (value) {
        //     return numberRegex.test(`${value ?? ""}`);
        // }),
        stampDuty: yup
            .string()
            .transform(function (value) { return `${value ?? ""}`?.replaceAll(',', '') })
            // .required('Stamp Duty is required')
            .test('is-number', 'Stamp Duty should be a valid number', function (value) {
                if (`${value ?? ""}`.length === 0) return true

                return !Number.isNaN(Number(value))
            }),
        // .test('greater-than-zero', "Stamp Duty should be greater than 0", function (value) {
        //     return Number(value) > 0
        // }),
        // .test('whole-number', 'Stamp Duty should be a valid number', function (value) {
        //     return numberRegex.test(`${value ?? ""}`);
        // }),
        // accruedInterest: yup
        //     .string()
        //     .transform(function (value) { return `${value ?? ""}`?.replaceAll(',', '')})
        //     .required('Accrued Interest is required')
        //     .test('is-number', 'Accrued Interest should be a valid number', function (value) { return !Number.isNaN(Number(value)) }),
        settlementAmount: yup
            .string()
            .transform(function (value) { return `${value ?? ""}`?.replaceAll(',', '') })
            .test('positive-number', 'Settlement Amount should be a positive number', function (value) {
                if (`${value ?? ""}`.length === 0) return true

                return Number(value ?? "") >= 0
            }),
        // .required('Settlement Amount is required')
        // .test('is-number', 'Settlement Amount should be a valid number', function (value) { return !Number.isNaN(Number(value)) }),
        remarks: yup
            .string()
            .required("Remarks is required")
            .max(200, 'Max 200 characters allowed'),
        // Settlement Details
        settlementMethod: yup
            .string()
            .required("Settlement Method is required"),
        settlementDate: yup
            .date()
            .required("Settlement Date is required")
            .min(dayjs().startOf('day').toDate(), `Settlement Date cannot be less than today's date`),
        referenceNumber: yup
            .string()
            // .required("Reference Number is required")
            .matches(alphaNumeric, 'Invalid Reference Number'),
        // settlementBank: yup
        //     .string()
        //     .required("Settlement Bank is required"),
        settlementBankNumber: yup
            .object()
            .nullable()
            .test(
                "settlement-bank-number-required",
                "Settlement Bank Number is required",
                function (value) {
                    return !!value
                }),
        settlementDetailsDepositoryName: yup
            .string()
            .required("Depository Name is required"),
        settlementDetailsDpName: yup
            .object()
            .nullable()
            .test(
                "dp_name_required",
                "DP Name is required",
                function (value) {
                    return !!value
                }),
        settlementDetailsDpId: yup
            .string()
            .required("DP ID is required"),
        settlementDetailsClientId: yup
            .string()
            .required("Client ID is required"),
        // Order Supporting Document
        documents: yup.array().of(
            yup.object().shape({
                documentId: yup.string().required("Document Upload is required"),
                documentType: yup.string().required("Document Type is required")
            })
        )
    },
    "Margin Money": {
        // Client Details
        clientName: yup
            .object()
            .nullable()
            .test(
                "client-name-required",
                "Client Name is required",
                function (value) { return !!value }
            ),
        account: yup.string().required("Account is required"),
        // accountType: yup.string().required('Account Type is required'), 
        investorBankAccount: yup
            .object()
            .nullable()
            .test(
                "investor-bank-account-required",
                "Investor Bank Account is required",
                function (value) { return !!value }
            ),
        // availableBankBalance: yup
        //     .number()
        //     .required('Available Bank Balance is required')
        //     .typeError("Available Bank Balance should be a valid number"),
        // emailId: yup
        //     .string()
        //     .required("Email ID is required")
        //     .matches(emailRegex, 'Invalid Email ID'), 
        // srm: yup.string().required("RM is required"),
        // Order Details
        // orderNumber: yup.string().required("Order Number is required"), 
        orderDate: yup
            .date()
            .required("Order Date is required")
            .max(dayjs().toDate(), `Order Date cannot be greater than today's date`),
        transactionType: yup
            .string()
            .required("Transaction Type is required"),
        amount: yup
            .string()
            .transform(function (value) { return `${value ?? ""}`?.replaceAll(',', '') })
            .required('Amount is required')
            .test('is-number', 'Amount should be a valid number', function (value) { return !Number.isNaN(Number(value)) }),
        // .test('whole-number', 'Amount should be a valid number', function (value) {
        //     return numberRegex.test(`${value ?? ""}`);
        // }),
        broker: yup
            .object()
            .nullable()
            .test(
                "broker-required",
                "Broker is required",
                function (value) { return !!value }
            ),
        remarks: yup
            .string()
            .required("Remarks is required")
            .max(200, 'Max 200 characters allowed'),
        // Settlement Details
        settlementMethod: yup
            .string()
            .required("Settlement Method is required"),
        settlementDate: yup
            .date()
            .required("Settlement Date is required")
            .min(dayjs().startOf('day').toDate(), `Settlement Date cannot be less than today's date`),
        referenceNumber: yup
            .string()
            // .required("Reference Number is required")
            .matches(alphaNumeric, 'Invalid Reference Number'),
        // settlementBank: yup
        //     .string()
        //     .required("Settlement Bank is required"),
        settlementBankNumber: yup
            .object()
            .nullable()
            .test(
                "settlement-bank-number-required",
                "Settlement Bank Number is required",
                function (value) {
                    return !!value
                }),
        // Order Supporting Document
        documents: yup.array().of(
            yup.object().shape({
                documentId: yup.string().required("Document Upload is required"),
                documentType: yup.string().required("Document Type is required")
            })
        )
    },
    "Corpus In/Out": {
        // Client Details
        clientName: yup
            .object()
            .nullable()
            .test(
                "client-name-required",
                "Client Name is required",
                function (value) { return !!value }
            ),
        account: yup
            .string()
            .required("Account is required"),
        // accountType: yup.string().required("Account Type is required"), 
        investorBankAccount: yup
            .object()
            .nullable()
            .test(
                "investor-bank-account-required",
                "Investor Bank Account is required",
                function (value) { return !!value }
            ),
        // availableBankBalance: yup
        //     .number()
        //     .required('Available Bank Balance is required')
        //     .typeError("Available Bank Balance should be a valid number"), 
        // emailId: yup
        //     .string()
        //     .required("Email ID is required")
        //     .matches(emailRegex, 'Invalid Email ID'),
        // srm: yup
        //     .string()
        //     .required("RM is required"),
        // Order Details
        // orderNumber: yup.string().required("Order Number is required"),
        orderDate: yup
            .date()
            .required("Order Date is required")
            .max(dayjs().toDate(), `Order Date cannot be greater than today's date`),
        transactionType: yup
            .string()
            .required("Transaction Type is required"),
        amount: yup
            .string()
            .transform(function (value) { return `${value ?? ""}`?.replaceAll(',', '') })
            .required('Amount is required')
            .test('is-number', 'Amount should be a valid number', function (value) { return !Number.isNaN(Number(value)) }),
        // .test('whole-number', 'Amount should be a valid number', function (value) {
        //     return numberRegex.test(`${value ?? ""}`);
        // }),
        remarks: yup
            .string()
            .required("Remarks is required")
            .max(200, 'Max 200 characters allowed'),
        // Settlement Details
        settlementMethod: yup.string().required('Settlement Method is required'),
        settlementDate: yup
            .date()
            .required("Settlement Date is required")
            .min(dayjs().startOf('day').toDate(), `Settlement Date cannot be less than today's date`),
        referenceNumber: yup
            .string()
            // .required('Reference Number is required')
            .matches(alphaNumeric, 'Invalid Reference Number'),
        // settlementBank: yup
        //     .string()
        //     .required('Settlement Bank is required'), 
        settlementBankNumber: yup
            .object()
            .nullable()
            .test(
                "settlement-bank-number-required",
                "Settlement Bank Number is required",
                function (value) {
                    return !!value
                })
            .test("cannot-be-same-as-investor-bank-account", 'Settlement Bank Number cannot be same as Investor Bank Account', function (value, context) {
                const { parent } = context;
                return `${parent?.investorBankAccount?.bankAccNo ?? ""}` !== `${value?.bankAccNo ?? ""}`
            }),
        // Order Supporting Document
        documents: yup.array().of(
            yup.object().shape({
                documentId: yup.string().required("Document Upload is required"),
                documentType: yup.string().required("Document Type is required")
            })
        )
    },
    "Fee Payment": {
        // Client Details
        clientName: yup
            .object()
            .nullable()
            .test(
                "client-name-required",
                "Client Name is required",
                function (value) { return !!value }
            ),
        account: yup
            .string()
            .required("Account is required"),
        // accountType: yup.string().required("Account Type is required"), 
        investorBankAccount: yup
            .object()
            .nullable()
            .test(
                "investor-bank-account-required",
                "Investor Bank Account is required",
                function (value) { return !!value }
            ),
        // availableBankBalance: yup
        //     .number()
        //     .required('Available Bank Balance is required')
        //     .typeError("Available Bank Balance should be a valid number"), 
        // emailId: yup
        //     .string()
        //     .required("Email ID is required")
        //     .matches(emailRegex, 'Invalid Email ID'),
        // srm: yup
        //     .string()
        //     .required("RM is required"),
        // Order Details
        // orderNumber: yup.string().required("Order Number is required"),
        orderDate: yup
            .date()
            .required("Order Date is required")
            .max(dayjs().toDate(), `Order Date cannot be greater than today's date`),
        transactionType: yup
            .string()
            .required("Transaction Type is required"),
        amount: yup
            .string()
            .transform(function (value) { return `${value ?? ""}`?.replaceAll(',', '') })
            .required('Amount is required')
            .test('is-number', 'Amount should be a valid number', function (value) { return !Number.isNaN(Number(value)) }),
        // .test('whole-number', 'Amount should be a valid number', function (value) {
        //     return numberRegex.test(`${value ?? ""}`);
        // }),
        remarks: yup
            .string()
            .required("Remarks is required")
            .max(200, 'Max 200 characters allowed'),
        // Settlement Details
        settlementMethod: yup.string().required('Settlement Method is required'),
        settlementDate: yup
            .date()
            .required("Settlement Date is required")
            .min(dayjs().startOf('day').toDate(), `Settlement Date cannot be less than today's date`),
        referenceNumber: yup
            .string()
            // .required('Reference Number is required')
            .matches(alphaNumeric, 'Invalid Reference Number'),
        // settlementBank: yup
        //     .string()
        //     .required('Settlement Bank is required'), 
        settlementBankNumber: yup
            .object()
            .nullable()
            .test(
                "settlement-bank-number-required",
                "Settlement Bank Number is required",
                function (value) {
                    return !!value
                })
            .test("cannot-be-same-as-investor-bank-account", 'Settlement Bank Number cannot be same as Investor Bank Account', function (value, context) {
                const { parent } = context;
                return `${parent?.investorBankAccount?.bankAccNo ?? ""}` !== `${value?.bankAccNo ?? ""}`
            }),
        // Order Supporting Document
        documents: yup.array().of(
            yup.object().shape({
                documentId: yup.string().required("Document Upload is required"),
                documentType: yup.string().required("Document Type is required")
            })
        )
    }
}
